import { baasProxy } from "./baasConsoleClient";
import { BAAS_COUNTRY_CODES } from "./baasBusinessAccountApi";
import { countryCodeToFlagEmoji, FALLBACK_PHONE_DIAL_OPTIONS } from "./baasPhoneParts";

const FALLBACK_COUNTRY_NAMES = Object.fromEntries(
  FALLBACK_PHONE_DIAL_OPTIONS.map((entry) => [entry.code, entry.name]),
);

const FALLBACK_COUNTRY_OPTIONS = BAAS_COUNTRY_CODES.map((code) => ({
  code,
  name: FALLBACK_COUNTRY_NAMES[code] || code,
  phoneCode: code === "BR" ? "55" : code === "US" || code === "CA" ? "1" : "",
}));

const countryCache = new Map();

const normalizeCountryOptions = (countries = []) =>
  countries
    .map((entry) => {
      const code = String(entry?.code ?? entry?.iso2 ?? "").trim().toUpperCase();
      const name = String(entry?.name ?? code).trim();
      const phoneCode = String(entry?.phone_code ?? entry?.phoneCode ?? "")
        .replace(/\D/g, "")
        .trim();
      if (!code) {
        return null;
      }
      return { code, name, phoneCode: phoneCode || undefined };
    })
    .filter(Boolean);

export const getFallbackCountryOptions = () => [...FALLBACK_COUNTRY_OPTIONS];

export const formatCountryOptionLabel = ({ name, code }) => {
  const flag = countryCodeToFlagEmoji(code);
  const label = `${name} (${code})`;
  return flag ? `${flag} ${label}` : label;
};

/**
 * Loads platform catalog countries (DB `Countries` table) for console dropdowns.
 * Falls back to static BAAS_COUNTRY_CODES when the request fails.
 */
export const fetchBaasPlatformCountries = async (instance, { signal } = {}) => {
  const cacheKey = instance?.id ?? instance?.baasInstanceId ?? "default";
  if (countryCache.has(cacheKey)) {
    return countryCache.get(cacheKey);
  }

  try {
    const data = await baasProxy({
      instance,
      method: "GET",
      baasPath: "platform/options",
      signal,
    });
    const options = normalizeCountryOptions(data?.countries);
    const resolved = options.length > 0 ? options : FALLBACK_COUNTRY_OPTIONS;
    countryCache.set(cacheKey, resolved);
    return resolved;
  } catch {
    return FALLBACK_COUNTRY_OPTIONS;
  }
};
